import React, { useMemo } from 'react'
import { Training, Volunteer, VolunteerProfileMetaProps } from '../data/types'
import { Card } from './ui/Card'
import { Button } from './ui/Button'
import { DownloadIcon, EditIcon, PlusIcon, UploadIcon, XIcon } from '../icons'
import { AVAILABILITY_OPTIONS, VOLUNTEER_FOR_VISIT_MAP } from '../data'
import { useVisitAnnounced } from '../hooks/useGlobalSetting'
import { API_BASE_URL } from '../data'
import { formatDateOnly } from '../utils/dates'

type Props = {
    volunteer: Volunteer
    meta: VolunteerProfileMetaProps
    onEdit?: () => void
    onDelete?: (training: Training) => void
    onAssign?: () => void
    onUploadDocuments?: () => void
    adminSide?: boolean
}
export const VolunteerProfileCard = (props: Props) => {
    const { volunteer, meta, onEdit, onDelete, onAssign, adminSide, onUploadDocuments } = props
    const { value: visitAnnounced, loading: loadingVisitAnnounced } = useVisitAnnounced()

    // Calculate age from date of birth, fallback to age field
    const age = useMemo(() => {
        // Try to calculate from DOB first
        if (volunteer.dob) {
            const dob = new Date(volunteer.dob)
            if (!isNaN(dob.getTime())) {
                const today = new Date()
                let years = today.getFullYear() - dob.getFullYear()
                const monthDiff = today.getMonth() - dob.getMonth()
                const dayDiff = today.getDate() - dob.getDate()
                if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) {
                    years--
                }
                if (years >= 0) return years
            }
        }
        // Fallback to age field if DOB is not available
        if (volunteer.age !== null && volunteer.age !== undefined) {
            return volunteer.age
        }
        return null
    }, [volunteer.dob, volunteer.age])

    // Calculate Date string
    const extractDate = (dateTime: string): string => {
        if (!dateTime) return "Invalid Date";

        const date = new Date(dateTime);

        if (isNaN(date.getTime())) return "Invalid Date";

        const day = String(date.getDate()).padStart(2, "0");
        const month = String(date.getMonth() + 1).padStart(2, "0"); // Months are 0-based
        const year = date.getFullYear();

        return `${day}-${month}-${year}`;
    }

    // Format date for display
    const formatDate = (dateStr: string | null | undefined) => {
        return formatDateOnly(dateStr, 'en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        })
    }


    // Helper component for displaying field
    const Field = ({ label, value }: { label: string; value: string | number | null | undefined }) => {
        return (
            <div className="space-y-1">
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">{label}</p>
                <p className="text-gray-900 dark:text-gray-100">{value || 'N/A'}</p>
            </div>
        )
    }

    const volunteerLanguages = volunteer.languages?.split(",").map(lang => lang.trim()).filter(lang => lang.length > 0) || [];

    const currentAvailability = useMemo(() => {
        const availabilityOptions = new Set<string>(AVAILABILITY_OPTIONS)
        return volunteer.availability?.filter(avail => availabilityOptions.has(avail)) || []
    }, [volunteer.availability])

    // Detect if we have limited data (view_volunteer permission only vs list_volunteers)
    // Limited data doesn't include email, phone, flags, preferences, trainings, lead positions, etc.
    // Limited data users don't receive email/phone - use this to detect limited view
    const isLimitedData = volunteer.email === undefined;

    const handleDownload = async (file_key: string, filename: string) => {

        try {
            const response = await fetch(`${API_BASE_URL}/file/${encodeURIComponent(file_key)}`, {
                credentials: 'include'
            });

            if (!response.ok) {
                console.error("Failed to fetch download URL");
                return;
            }

            const result = await response.json();

            const downloadUrl = result.data?.url || result.url;

            if (!downloadUrl) throw new Error("No URL returned");

            const link = document.createElement('a');
            link.href = downloadUrl;
            link.download = filename;
            link.target = '_blank';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
        catch (err) {
            alert("Error while downloading document")
        }
    };

    console.log("Volunteer: ", volunteer);

    return (
        <Card className="mb-8">
            {/* Header Section */}
            <div className="relative p-8 bg-repeat bg-tile" style={{ backgroundImage: 'url(/header_tile_gold.png)' }}>
                <div className="absolute inset-0 bg-gradient-to-r from-primary-900/50 to-primary-400/30"></div>
                <div className="absolute top-4 end-4 z-20 flex items-center gap-2">
                    {onUploadDocuments && (
                        <Button variant='outline' size='sm' className='bg-white/90' onClick={() => onUploadDocuments()}>
                            <UploadIcon />
                            Upload Documents
                        </Button>
                    )}
                    {onEdit && (
                        <Button
                            onClick={onEdit}
                            variant="outline"
                            size="sm"
                            className={volunteer.profile_submitted_at ? "bg-white/90 [&]:hover:bg-white" : "bg-warn-500 [&]:hover:bg-warn-600 text-white animate-pulse"}
                        >
                            <EditIcon className="w-4 h-4 me-1" />
                            {volunteer.profile_submitted_at ? meta.editProfileButton : (visitAnnounced ? 'Register for Didar' : meta.completeProfileButton)}
                            {!volunteer.profile_submitted_at && (
                                <span className="ms-2 inline-flex items-center justify-center w-2 h-2 bg-white rounded-full">
                                    <span className="sr-only">Action required</span>
                                </span>
                            )}
                        </Button>
                    )}
                </div>
                <div className="relative z-10">
                    <h1 className="text-4xl font-bold text-white mb-2">
                        {volunteer.first_name} {volunteer.last_name}
                    </h1>
                    {!isLimitedData && <p className="text-primary-100 text-lg">{volunteer.email}</p>}
                    {/* {volunteer.csid && (
                        <p className="text-primary-200 text-sm mt-1">{meta.csid}: {volunteer.csid}</p>
                    )} */}

                </div>

            </div>

            {/* Profile Information */}
            <div className="p-6">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-6">
                    {meta.personalInfoSection}
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    <Field label={meta.first_name} value={volunteer.first_name} />
                    <Field label={meta.last_name} value={volunteer.last_name} />
                    {volunteer.second_last_name && (
                        <Field label={meta.second_last_name} value={volunteer.second_last_name} />
                    )}
                    <Field label={meta.gender} value={volunteer.gender} />

                    {/* {adminSide && !isLimitedData && <Field label={meta.dob} value={formatDate(volunteer.dob)} />} */}
                    {adminSide && !isLimitedData && <Field label={meta.age} value={age ?? volunteer.age} />}
                    {!isLimitedData && <Field label={meta.email} value={volunteer.email} />}
                    {!isLimitedData && <Field label={meta.phone} value={volunteer.phone} />}
                    {/* {volunteer.country_code && (
                        <Field label={meta.country_code} value={volunteer.country_code} />
                    )} */}
                    {adminSide && <Field label={meta.country} value={volunteer.country} />}
                    {adminSide && volunteer.country_of_birth && (
                        <Field label={meta.country_of_birth} value={volunteer.country_of_birth} />
                    )}
                    {volunteer.family_origin && adminSide && (
                        <Field label={meta.family_origin} value={volunteer.family_origin} />
                    )}
                    <Field label={meta.jamat_khana} value={volunteer.jamat_khana} />

                </div>


                {/* Location Information */}
                {/* <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mt-8 mb-6">
                    {meta.locationSection}
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    <Field label={meta.country} value={volunteer.country} />
                    {adminSide && volunteer.country_of_birth && (
                        <Field label={meta.country_of_birth} value={volunteer.country_of_birth} />
                    )}
                    <Field label={meta.jamat_khana} value={volunteer.jamat_khana} />
                    {adminSide && volunteer.service_location && (
                        <Field label={meta.service_location} value={volunteer.service_location} />
                    )}
                    {volunteer.service_record && (
                        <Field label={meta.service_record} value={volunteer.service_record} />
                    )}
                </div> */}

                {/* Additional Information */}
                <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mt-8 mb-6">
                    {meta.additionalInfoSection}
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {volunteer.service_record && (
                        <Field label={meta.service_record} value={volunteer.service_record} />
                    )}

                    {/* {adminSide && volunteer.service_location && (
                                <Field label={meta.service_location} value={volunteer.service_location} />
                            )} */}

                    {/* {volunteer.multi_faith_family !== undefined && (
                                <div className="space-y-1">
                                    <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                                        {meta.multi_faith_family}
                                    </p>
                                    <p className="text-gray-900 dark:text-gray-100">
                                        {volunteer.multi_faith_family ? meta.yes : meta.no}
                                    </p>
                                </div>
                            )} */}
                    {!isLimitedData && volunteer.ismaili !== undefined && (
                        <div className="space-y-1">
                            <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                                {meta.ismaili}
                            </p>
                            <p className="text-gray-900 dark:text-gray-100">
                                {volunteer.ismaili ? meta.yes : meta.no}
                            </p>
                        </div>
                    )}
                    {adminSide && !isLimitedData && volunteer.registered_volunteer !== undefined && (
                        <div className="space-y-1">
                            <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                                {meta.registered_volunteer_label}
                            </p>
                            <p className="text-gray-900 dark:text-gray-100">
                                {volunteer.registered_volunteer ? meta.yes : meta.no}
                            </p>
                        </div>
                    )}
                    {adminSide && !isLimitedData && volunteer.eligible_for_duty !== undefined && (
                        <div className="space-y-1">
                            <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                                {meta.eligible_for_duty_label}
                            </p>
                            <p className="text-gray-900 dark:text-gray-100">
                                {volunteer.eligible_for_duty ? meta.yes : meta.no}
                            </p>
                        </div>
                    )}
                    {visitAnnounced && volunteer.volunteer_for_visit !== undefined && (
                        <div className="space-y-1">
                            <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                                {meta.volunteer_for_visit_label}
                            </p>
                            <p className="text-gray-900 dark:text-gray-100">
                                {VOLUNTEER_FOR_VISIT_MAP[volunteer.volunteer_for_visit]}
                            </p>
                        </div>
                    )}
                </div>

                {/* Volunteer Negative List Member */}
                {/* {volunteer.negative_list_members && volunteer.negative_list_members.length > 0 && (
                    <div>
                        <div className="mt-6">
                            <div className="space-y-1">
                                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                                    {meta.negativeListLabel}
                                </p>
                                <div className="flex flex-wrap gap-2">
                                    {volunteer.negative_list_members.map((member, index) => (
                                        <span
                                            key={index}
                                            className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-primary-100 text-primary-800 dark:bg-primary-900 dark:text-primary-200"
                                        >
                                            {member}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                )} */}

                {visitAnnounced && volunteer.volunteer_for_visit === 1 && currentAvailability.length > 0 && (
                    <div className="mt-6">
                        <div className="space-y-1">
                            <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                                {meta.availability_label}
                            </p>
                            <div className="flex flex-wrap gap-2">
                                {currentAvailability.map((avail, index) => (
                                    <span
                                        key={index}
                                        className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-primary-100 text-primary-800 dark:bg-primary-900 dark:text-primary-200"
                                    >
                                        {avail}
                                    </span>
                                ))}
                            </div>
                        </div>
                    </div>
                )}



                <div className="mt-6">
                    {volunteer.support_needs && (
                        <div className="space-y-1">
                            <h2 className="text-sm font-medium text-gray-500 dark:text-gray-400">
                                {meta.support_needs}
                            </h2>
                            <p className="text-gray-900 dark:text-gray-100 whitespace-pre-wrap">
                                {volunteer.support_needs}
                            </p>
                        </div>
                    )}
                </div>

                {/* Flags Section */}
                <div className="border-t dark:border-gray-500 my-8 pt-8">
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">
                        {meta.flagsSection}
                    </h2>

                    <div className="flex flex-wrap gap-2">
                        {volunteer.flags?.length ? (
                            volunteer.flags.map(flag => (
                                <span
                                    key={flag.id}
                                    className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-success-100 text-success-800 dark:bg-success-900 dark:text-success-200"
                                >
                                    {flag.name}
                                </span>
                            ))
                        ) : (
                            <p className="text-gray-500 dark:text-gray-400 italic text-sm">
                                No flags selected
                            </p>
                        )}
                    </div>
                </div>


                {/* Preferences Section */}
                {/* {adminSide && !isLimitedData && <div className="border-t dark:border-gray-500 my-8 pt-8">
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">
                        {meta.preferencesSection}
                    </h2>

                    <div className="flex flex-wrap gap-2">
                        {volunteer.preferences?.length ? (
                            volunteer.preferences.map(preference => (
                                <span
                                    key={preference.id}
                                    className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-primary-100 text-primary-800 dark:bg-primary-900 dark:text-primary-200"
                                >
                                    {preference.name}
                                </span>
                            ))
                        ) : (
                            <p className="text-gray-500 dark:text-gray-400 italic text-sm">
                                No preferences selected
                            </p>
                        )}
                    </div>
                </div>} */}


                {/* Languages Section */}
                <div className="border-t dark:border-gray-500 my-8 pt-8">
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">
                        {meta.languageSection}
                    </h2>

                    <div className="flex flex-wrap gap-2">
                        {volunteerLanguages.length > 0 ? (
                            volunteerLanguages.map((language, index) => (
                                <span
                                    key={index}
                                    className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-success-100 text-primary-500 dark:bg-primary-500 dark:text-white  "
                                >
                                    {language}
                                </span>
                            )))
                            : (
                                <p className="text-gray-500 dark:text-gray-400 italic text-sm">
                                    No Languages Selected
                                </p>
                            )}
                    </div>
                </div>

                {/* Training Section */}
                {adminSide &&
                    <div className="border-t dark:border-gray-500 my-8 pt-8">

                        <div className='flex items-center justify-between mb-4'>
                            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">
                                {meta.trainingsSection}
                            </h2>

                            <Button color='primary' variant='solid' onClick={onAssign}>
                                <PlusIcon />
                                <span className="text-sm">Assign Training</span>
                            </Button>
                        </div>

                        {volunteer.trainings?.length ? (
                            <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
                                <table className="min-w-full text-sm">
                                    {/* Table Header */}
                                    <thead className="bg-gray-100 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                                        <tr>
                                            <th className="text-start px-4 py-3 font-semibold text-gray-700 dark:text-gray-300 w-1/2">Training Name</th>
                                            <th className="text-center px-4 py-3 font-semibold text-gray-700 dark:text-gray-300 w-1/6">Assigned On</th>
                                            <th className="text-center px-4 py-3 font-semibold text-gray-700 dark:text-gray-300 w-1/6">Status</th>
                                            <th className="text-end px-4 py-3 font-semibold text-gray-700 dark:text-gray-300 w-1/6">Action</th>
                                        </tr>
                                    </thead>

                                    {/* Table Rows */}
                                    <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                                        {volunteer.trainings.map(training => (
                                            <tr key={training.id} className="hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                                                {/* Training Name */}
                                                <td className="px-4 py-3 font-medium text-gray-900 dark:text-gray-100">
                                                    {training.name}
                                                </td>

                                                {/* Assigned At */}
                                                <td className="px-4 py-3 text-center text-gray-600 dark:text-gray-400">
                                                    {extractDate(training.assigned_at)}
                                                </td>

                                                {/* Status */}
                                                <td className="px-4 py-3 text-center">
                                                    <span
                                                        className={`px-2 py-1 rounded-full text-xs font-medium ${training.completion_status === 1
                                                            ? "bg-success-100 text-success-800 dark:bg-success-900 dark:text-success-200"
                                                            : "bg-warn-100 text-warn-800 dark:bg-warn-900 dark:text-warn-200"
                                                            }`}
                                                    >
                                                        {training.completion_status === 1 ? "Completed" : "In Progress"}
                                                    </span>
                                                </td>

                                                {/* Action */}
                                                <td className="px-4 py-3 text-end">
                                                    <button
                                                        className="text-danger-600 hover:text-danger-800 dark:text-danger-400 dark:hover:text-danger-300 transition-colors"
                                                        onClick={() => onDelete(training)}
                                                        aria-label="Unassign training"
                                                        title="Unassign training"
                                                    >
                                                        <XIcon className="w-5 h-5" />
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <p className="text-gray-500 dark:text-gray-400 italic text-sm">
                                No trainings allocated
                            </p>
                        )}
                    </div>
                }

                {adminSide && (
                    <div className="border-t dark:border-gray-500 my-8 pt-8">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">
                                {meta.allocatedShiftsLabel}
                            </h2>
                        </div>

                        {/* Event Lead Allocations */}
                        {(volunteer.event_leads?.length ?? 0) > 0 &&
                            <div className="mb-8">
                                <h4 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-4">
                                    {meta.eventLeadLabel}
                                </h4>
                                <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
                                    <table className="min-w-full text-sm">
                                        <thead className="bg-gray-100 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                                            <tr>
                                                {/* Widths: 8/12 + 2/12 + 2/12 = 12/12 */}
                                                <th className="text-start px-4 py-3 font-semibold text-gray-700 dark:text-gray-300 w-8/12">
                                                    Event
                                                </th>
                                                <th className="text-end px-4 py-3 font-semibold text-gray-700 dark:text-gray-300 w-2/12 whitespace-nowrap">
                                                    Start Time
                                                </th>
                                                <th className="text-end px-4 py-3 font-semibold text-gray-700 dark:text-gray-300 w-2/12 whitespace-nowrap">
                                                    End Time
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                                            {(volunteer.event_leads?.length ?? 0) > 0 ? (
                                                volunteer.event_leads?.map((el, index) => (
                                                    <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                                                        <td className="px-4 py-3 font-medium text-gray-900 dark:text-gray-100 text-start">
                                                            {el.name}
                                                        </td>
                                                        <td className="px-4 py-3 font-medium text-gray-900 dark:text-gray-100 text-end whitespace-nowrap">
                                                            {el.start_time}
                                                        </td>
                                                        <td className="px-4 py-3 font-medium text-gray-900 dark:text-gray-100 text-end whitespace-nowrap">
                                                            {el.end_time}
                                                        </td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan={3} className="px-4 py-3 text-center text-gray-500 italic">No event lead allocations.</td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        }


                        {/* Area Lead Allocations */}
                        {((volunteer.areas_as_lead?.length ?? 0) > 0 || (volunteer.areas_as_deputy_lead?.length ?? 0) > 0) &&
                            <div className="mb-8">
                                <h4 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-4">
                                    {meta.areaLeadLabel}
                                </h4>
                                <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
                                    <table className="min-w-full text-sm">
                                        <thead className="bg-gray-100 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                                            <tr>
                                                <th className="text-start px-4 py-3 font-semibold text-gray-700 dark:text-gray-300 w-3/12">Event</th>
                                                <th className="text-start px-4 py-3 font-semibold text-gray-700 dark:text-gray-300 w-3/12">Area</th>
                                                <th className="text-start px-4 py-3 font-semibold text-gray-700 dark:text-gray-300 w-2/12">Role</th>
                                                <th className="text-end px-4 py-3 font-semibold text-gray-700 dark:text-gray-300 w-2/12 whitespace-nowrap">Start Time</th>
                                                <th className="text-end px-4 py-3 font-semibold text-gray-700 dark:text-gray-300 w-2/12 whitespace-nowrap">End Time</th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                                            {/* Leads */}
                                            {volunteer.areas_as_lead?.map((al, index) => (
                                                <tr key={`lead-${index}`} className="hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                                                    <td className="px-4 py-3 font-medium text-gray-900 dark:text-gray-100 text-start">{al.event.name}</td>
                                                    <td className="px-4 py-3 font-medium text-gray-900 dark:text-gray-100 text-start">{al.name}</td>
                                                    <td className="px-4 py-3 font-medium text-gray-900 dark:text-gray-100 text-start">Lead</td>
                                                    <td className="px-4 py-3 font-medium text-gray-900 dark:text-gray-100 text-end whitespace-nowrap">{al.event.start_time}</td>
                                                    <td className="px-4 py-3 font-medium text-gray-900 dark:text-gray-100 text-end whitespace-nowrap">{al.event.end_time}</td>
                                                </tr>
                                            ))}
                                            {/* Deputy Leads */}
                                            {volunteer.areas_as_deputy_lead?.map((al, index) => (
                                                <tr key={`deputy-${index}`} className="hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                                                    <td className="px-4 py-3 font-medium text-gray-900 dark:text-gray-100 text-start">{al.event.name}</td>
                                                    <td className="px-4 py-3 font-medium text-gray-900 dark:text-gray-100 text-start">{al.name}</td>
                                                    <td className="px-4 py-3 font-medium text-gray-900 dark:text-gray-100 text-start">Deputy Lead</td>
                                                    <td className="px-4 py-3 font-medium text-gray-900 dark:text-gray-100 text-end whitespace-nowrap">{al.event.start_time}</td>
                                                    <td className="px-4 py-3 font-medium text-gray-900 dark:text-gray-100 text-end whitespace-nowrap">{al.event.end_time}</td>
                                                </tr>
                                            ))}
                                            {(volunteer.areas_as_lead?.length ?? 0) === 0 && (volunteer.areas_as_deputy_lead?.length ?? 0) === 0 && (
                                                <tr><td colSpan={5} className="px-4 py-3 text-center text-gray-500 italic">No area lead allocations.</td></tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        }

                        {/* Shift Lead Allocations */}
                        {((volunteer.shifts_as_lead?.length ?? 0) > 0 || (volunteer.shifts_as_deputy_lead?.length ?? 0) > 0) &&
                            <div className="mb-8">
                                <h4 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-4">
                                    {meta.shiftLeadLabel}
                                </h4>
                                <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
                                    <table className="min-w-full text-sm">
                                        <thead className="bg-gray-100 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                                            <tr>
                                                <th className="text-start px-4 py-3 font-semibold text-gray-700 dark:text-gray-300 w-2/12">Event</th>
                                                <th className="text-start px-4 py-3 font-semibold text-gray-700 dark:text-gray-300 w-3/12">Area</th>
                                                <th className="text-start px-4 py-3 font-semibold text-gray-700 dark:text-gray-300 w-2/12">Shift</th>
                                                <th className="text-start px-4 py-3 font-semibold text-gray-700 dark:text-gray-300 w-2/12">Role</th>
                                                <th className="text-end px-4 py-3 font-semibold text-gray-700 dark:text-gray-300 w-1/12 whitespace-nowrap">Start Time</th>
                                                <th className="text-end px-4 py-3 font-semibold text-gray-700 dark:text-gray-300 w-2/12 whitespace-nowrap">End Time</th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                                            {/* Leads */}
                                            {volunteer.shifts_as_lead?.map((sl, index) => (
                                                <tr key={`lead-${index}`} className="hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                                                    <td className="px-4 py-3 font-medium text-gray-900 dark:text-gray-100 text-start">{sl.area.event.name}</td>
                                                    <td className="px-4 py-3 font-medium text-gray-900 dark:text-gray-100 text-start">{sl.area.name}</td>
                                                    <td className="px-4 py-3 font-medium text-gray-900 dark:text-gray-100 text-start">{sl.title}</td>
                                                    <td className="px-4 py-3 font-medium text-gray-900 dark:text-gray-100 text-start">Lead</td>
                                                    <td className="px-4 py-3 font-medium text-gray-900 dark:text-gray-100 text-end whitespace-nowrap">{sl.start_time}</td>
                                                    <td className="px-4 py-3 font-medium text-gray-900 dark:text-gray-100 text-end whitespace-nowrap">{sl.end_time}</td>
                                                </tr>
                                            ))}
                                            {/* Deputy Leads */}
                                            {volunteer.shifts_as_deputy_lead?.map((sl, index) => (
                                                <tr key={`deputy-${index}`} className="hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                                                    <td className="px-4 py-3 font-medium text-gray-900 dark:text-gray-100 text-start">{sl.area.event.name}</td>
                                                    <td className="px-4 py-3 font-medium text-gray-900 dark:text-gray-100 text-start">{sl.area.name}</td>
                                                    <td className="px-4 py-3 font-medium text-gray-900 dark:text-gray-100 text-start">{sl.title}</td>
                                                    <td className="px-4 py-3 font-medium text-gray-900 dark:text-gray-100 text-start">Deputy Lead</td>
                                                    <td className="px-4 py-3 font-medium text-gray-900 dark:text-gray-100 text-end whitespace-nowrap">{sl.start_time}</td>
                                                    <td className="px-4 py-3 font-medium text-gray-900 dark:text-gray-100 text-end whitespace-nowrap">{sl.end_time}</td>
                                                </tr>
                                            ))}
                                            {(volunteer.shifts_as_lead?.length ?? 0) === 0 && (volunteer.shifts_as_deputy_lead?.length ?? 0) === 0 && (
                                                <tr><td colSpan={6} className="px-4 py-3 text-center text-gray-500 italic">No shift lead allocations.</td></tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        }

                        {/* Others (General Shifts) */}
                        <div>
                            <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-4">
                                {meta.otherAllocationLabel}
                            </h2>
                            {(volunteer.allocations?.length ?? 0) > 0 ? (
                                <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
                                    <table className="min-w-full text-sm">
                                        <thead className="bg-gray-100 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                                            <tr>
                                                <th className="text-start px-4 py-3 font-semibold text-gray-700 dark:text-gray-300 w-4/12">Event</th>
                                                <th className="text-start px-4 py-3 font-semibold text-gray-700 dark:text-gray-300 w-2/12">Area</th>
                                                <th className="text-start px-4 py-3 font-semibold text-gray-700 dark:text-gray-300 w-2/12">Shift</th>
                                                <th className="text-end px-4 py-3 font-semibold text-gray-700 dark:text-gray-300 w-1/12 whitespace-nowrap">Start Time</th>
                                                <th className="text-end px-4 py-3 font-semibold text-gray-700 dark:text-gray-300 w-1/12 whitespace-nowrap">End Time</th>
                                                <th className="text-end px-4 py-3 font-semibold text-gray-700 dark:text-gray-300 w-2/12">Status</th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                                            {volunteer.allocations.map(allocation => {
                                                let statusText = "Pending";
                                                let statusClass = 'bg-warn-100 text-warn-800 dark:bg-warn-900 dark:text-warn-200';

                                                if (allocation.allocation_times && allocation.allocation_times.length > 0) {
                                                    const allocationTime = allocation.allocation_times[0];
                                                    if (allocationTime.status === true) {
                                                        statusText = "Accepted";
                                                        statusClass = 'bg-success-100 text-success-800 dark:bg-success-900 dark:text-success-200';
                                                    } else if (allocationTime.status === false || allocationTime.status === 0) {
                                                        statusText = "Declined";
                                                        statusClass = 'bg-danger-100 text-danger-800 dark:bg-danger-900 dark:text-danger-200';
                                                    }
                                                }

                                                return (
                                                    <tr key={allocation.id} className="hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                                                        <td className="px-4 py-3 font-medium text-gray-900 dark:text-gray-100 text-start">{allocation.shift.area.event.name}</td>
                                                        <td className="px-4 py-3 font-medium text-gray-900 dark:text-gray-100 text-start">{allocation.shift.area.name}</td>
                                                        <td className="px-4 py-3 font-medium text-gray-900 dark:text-gray-100 text-start">{allocation.shift.title}</td>
                                                        <td className="px-4 py-3 font-medium text-gray-900 dark:text-gray-100 text-end whitespace-nowrap">{allocation.allocation_times[0].start_time}</td>
                                                        <td className="px-4 py-3 font-medium text-gray-900 dark:text-gray-100 text-end whitespace-nowrap">{allocation.allocation_times[0].end_time}</td>
                                                        <td className="px-4 py-3 text-sm text-end">
                                                            <span className={`inline-flex items-center px-2 py-1 rounded-lg text-xs ${statusClass}`}>
                                                                {statusText}
                                                            </span>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                <p className="text-gray-500 dark:text-gray-400 italic text-sm">No shifts have been allocated.</p>
                            )}
                        </div>

                    </div>
                )}
                {/* Uploaded Document Section */}
                <div className="border-t dark:border-gray-500 my-8 pt-8">
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">
                        {meta.uploadedDocumentsLabel}
                    </h2>

                    <div className="flex flex-col gap-2">
                        {/* Header Row */}
                        {(volunteer?.uploaded_documents || []).length > 0 && (
                            <div className="grid grid-cols-12 gap-4 px-4 py-2 text-sm font-semibold text-gray-500 dark:text-gray-400">
                                <div className="col-span-2">Type</div>
                                <div className="col-span-5">File Name</div>
                                <div className="col-span-3 text-end">Uploaded At</div>
                                <div className="col-span-2 text-end">Actions</div>
                            </div>
                        )}

                        {(volunteer?.uploaded_documents || []).length > 0 ? (
                            volunteer?.uploaded_documents.map((doc, index) => (
                                <div
                                    key={index}
                                    className="grid grid-cols-12 gap-4 items-center px-4 py-3 rounded-md bg-gray-50 text-primary-500 dark:bg-primary-500 dark:text-white"
                                >
                                    <div className="col-span-2 font-medium truncate">
                                        {doc.type}
                                    </div>

                                    <div className="col-span-5 truncate" title={doc.name}>
                                        {doc.name}
                                    </div>

                                    <div className="col-span-3 text-end text-sm opacity-80">
                                        {new Date(Number(doc.uploaded_at)).toLocaleDateString()}
                                    </div>

                                    <div className="col-span-2 flex justify-end">
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleDownload(doc.file_key, doc.name);
                                            }}
                                            className="p-2 text-primary-600 hover:text-primary-800 dark:text-white dark:hover:text-primary-200 hover:bg-primary-100 dark:hover:bg-primary-600 rounded-full transition-colors"
                                            title="Download Document"
                                            aria-label={`Download ${doc.name}`}
                                        >
                                            {/* Use your Download Icon here */}
                                            <DownloadIcon className="w-5 h-5" />
                                        </button>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <p className="text-gray-500 dark:text-gray-400 italic text-sm">
                                No Documents Uploaded.
                            </p>
                        )}

                        {/* Document Expiries */}
                        {(volunteer?.dbs_expiry || volunteer?.first_aid_expiry) && (
                            <div className="mt-4 max-w-3xl">
                                <div className="px-4 py-2 text-sm font-semibold text-gray-500 dark:text-gray-400">
                                    Recorded Expiry Dates
                                </div>

                                <div className="flex flex-col gap-2">
                                    {/* DBS Expiry */}
                                    {volunteer?.dbs_expiry && (
                                        <div className="grid grid-cols-12 gap-4 items-center px-4 py-3 rounded-md bg-gray-50 text-primary-500 dark:bg-primary-500 dark:text-white">
                                            <div className="col-span-7 font-medium">
                                                DBS Check
                                            </div>

                                            <div className="col-span-3 text-end text-sm opacity-90">
                                                Expires: {formatDateOnly(volunteer.dbs_expiry)}
                                            </div>

                                            <div className="col-span-2"></div>
                                        </div>
                                    )}

                                    {/* First Aid Expiry */}
                                    {volunteer?.first_aid_expiry && (
                                        <div className="grid grid-cols-12 gap-4 items-center px-4 py-3 rounded-md bg-gray-50 text-primary-500 dark:bg-primary-500 dark:text-white">
                                            <div className="col-span-7 font-medium">
                                                First Aid
                                            </div>

                                            <div className="col-span-3 text-end text-sm opacity-90">
                                                Expires: {formatDateOnly(volunteer.first_aid_expiry)}
                                            </div>

                                            <div className="col-span-2"></div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </Card >
    )
}


